import { injectable } from 'inversify';
import { createSelector } from '@reduxjs/toolkit';
import {
  AssessmentRepository,
  ConneqtRepository,
  IncidentRepository,
  PlayerRepository,
} from '../../state/reduxRepositories';
import { RootState, useAppSelector } from '../../state/hooks';
import symptomAssessmentSpecs from './symptom_assessment_v1.0.json';
import { Incident } from '../../state/reduxRepositories/incident/incident.interface';
import { Assessment } from '../../state/reduxRepositories/assessments/assessments.interfaces';
import { ViewIncidentQueryService } from '../viewIncident/viewIncident.queryParam.service';
import { NextIncident } from '../../helpers/nextIncident';
import { Player } from '../../state/reduxRepositories/players/player.interfaces';
import { ConneqtStatus } from '../../state/reduxRepositories/conneqt/conneqt.interfaces';

@injectable()
export class SymptomAssessmentDomainService {
  public constructor(
    private playerRepository: PlayerRepository,
    private conneqtRepository: ConneqtRepository,
    private assessmentRepository: AssessmentRepository,
    private incidentRepository: IncidentRepository,
    private viewIncidentQueryService: ViewIncidentQueryService,
  ) {}

  private createIncidentsByPlayerSelector = (): ((
    state: RootState,
  ) => Incident[]) => {
    return createSelector(
      this.viewIncidentQueryService.mergedQuerySelector,
      this.incidentRepository.selectors.resourcesSelector,
      (queryParam, incidents) => {
        return incidents.filter(
          (inc) => inc.playerId === queryParam.selectedPlayerId,
        );
      },
    );
  };

  private createCurrentIncidentSelector = (): ((
    state: RootState,
  ) => Incident | undefined) => {
    return createSelector(
      this.viewIncidentQueryService.mergedQuerySelector,

      this.incidentRepository.selectors.resourcesSelector,
      (queryParam, incidents) => {
        return incidents.find(
          (inc) => inc.id === queryParam.selectedIncidentId,
        );
      },
    );
  };

  private createSymptomAssessmentsByPlayerSelector = (): ((
    state: RootState,
  ) => Assessment[]) => {
    return createSelector(
      this.viewIncidentQueryService.mergedQuerySelector,

      this.assessmentRepository.selectors.resourcesSelector,
      (queryParam, assessments) => {
        return assessments
          .filter(
            (asx) =>
              asx.assessment.assessmentType ===
                symptomAssessmentSpecs.info.identifier &&
              asx.playerId === queryParam.selectedPlayerId,
          )
          .sort((a, b) => b?.createdAt?.localeCompare(a?.createdAt));
      },
    );
  };

  selectPlayerById = (playerId: string): Player | undefined => {
    return useAppSelector(
      createSelector(
        [this.playerRepository.selectors.resourcesSelector],
        (players) => players.find((player) => player.id === playerId),
      ),
    );
  };

  selectGuardiansForOrgPlayer = (playerId: string): string[] => {
    return useAppSelector(
      createSelector(
        [this.conneqtRepository.selectors.resourcesSelector],
        (conneqts) => {
          const conneqted = conneqts.filter(
            (conneqt) => conneqt.status === ConneqtStatus.Conneqted,
          );

          const responseEmailsFromSent = conneqted
            .filter((conneqt) => conneqt.requestPlayerId === playerId)
            .map((conneqt) => conneqt.responseEmail ?? '');

          const requestEmailsFromReceived = conneqted
            .filter((conneqt) => conneqt.responsePlayerId === playerId)
            .map((conneqt) => conneqt.requestUserEmail ?? '');

          return Array.from(
            new Set(
              [...responseEmailsFromSent, ...requestEmailsFromReceived].filter(
                (email) => email,
              ),
            ),
          );
        },
      ),
    );
  };

  selectSymptomAssessmentsForPlayerByIncident = (): Assessment[] => {
    return useAppSelector(
      createSelector(
        this.createSymptomAssessmentsByPlayerSelector(),
        this.createIncidentsByPlayerSelector(),
        this.createCurrentIncidentSelector(),
        (symptomAssessmentsByPlayer, incidentsByPlayer, currentIncident) => {
          const currentIncidentCreatedAt =
            currentIncident !== undefined &&
            (currentIncident.originalCreatedAt ?? currentIncident.createdAt);

          const nextIncident = NextIncident(
            incidentsByPlayer,
            currentIncidentCreatedAt,
          );

          // if there is no next incident,
          // return all symptom assessments created after incident created date
          if (!nextIncident) {
            return symptomAssessmentsByPlayer.filter((asx) => {
              const asxCreatedAt = asx.originalCreatedAt ?? asx.createdAt;
              return asxCreatedAt >= currentIncidentCreatedAt;
            });
          }

          const nextIncidentCreatedAt =
            nextIncident.originalCreatedAt ?? nextIncident.createdAt;

          // if there is a next incident,
          // return all symptom assessments between this incident and next incident dates
          return symptomAssessmentsByPlayer.filter((asx) => {
            const asxCreatedAt = asx.originalCreatedAt ?? asx.createdAt;

            return (
              asxCreatedAt >= currentIncidentCreatedAt &&
              asxCreatedAt < nextIncidentCreatedAt
            );
          });
        },
      ),
    );
  };
}
